import { CheckCircle2, Plug, Mail, Download, FileText, MessagesSquare, Ruler } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PageFooter } from "@/components/PageFooter";
import { getOwnerByCountry } from "@/data/contacts";
import { useLang } from "@/i18n";

const steps = [
  {
    step: 1,
    action:
      "Master F&B presents the FORA product portfolio to the Coca-Cola HBC BU (Business Unit). The presentation covers QR Menu, Tablet Menu, FORA POS, Videri Digital Signage, Reservation System, Loyalty, AI Sidekick, platform capabilities, data capabilities by product, and available market configurations.",
    owner: "Master F&B — Pero Pehar",
    ownerType: "master",
  },
  {
    step: 2,
    action:
      "Coca-Cola HBC BU reviews the proposal and defines the scope for their market — which products, which client segments, and the budget commitment. The budget is formally agreed and documented as part of the commercial terms.",
    owner: "Coca-Cola HBC BU",
    ownerType: "coke",
  },
  {
    step: 3,
    action:
      "A business agreement is signed between Coca-Cola HBC BU and Master F&B. The agreement includes commercial terms, the agreed budget commitment, and the product scope for the country. This is the formal start of the country programme.",
    owner: "Coca-Cola HBC BU + Master F&B",
    ownerType: "both",
  },
  {
    step: 4,
    action:
      "Coca-Cola HBC BU Owner decides who handles the printing agency for physical assets (QR cube stickers, signage). Two options: (A) Master F&B takes care of selecting and managing the printing agency end-to-end, or (B) Coca-Cola HBC BU uses their own preferred local agency — in which case Master F&B provides the print-ready artwork, technical specifications, and tooling so the agency can deliver. The choice is documented and locked in at this stage.",
    owner: "Coca-Cola HBC BU",
    ownerType: "coke",
  },
  {
    step: 5,
    action:
      "Technical integration is initiated. Master F&B technical team connects the FORA platform with software used by Coca-Cola HBC BU's Business Development representatives — CRM or any other software tools used at the BU. This includes outlet data sync, order flow mapping, analytics pipeline configuration, and SSO setup. All integrations are validated in a staging environment before go-live.",
    owner: "Master F&B technical team + Coca-Cola HBC BU IT",
    ownerType: "both",
    highlight: true,
  },
  {
    step: 6,
    action:
      "Both sides appoint a named country contact. Coca-Cola HBC BU appoints a single point of contact within their business unit who is responsible for the programme locally. On the Master F&B side, the SPOC is the dedicated Country Manager hired in the next step (Step 7) — your day-to-day operational owner. Pero Pehar (pero.pehar@master-fb.com) remains the commercial / contractual contact for partnership-level matters.",
    owner: "Coca-Cola HBC BU + Master F&B",
    ownerType: "both",
  },
  {
    step: 7,
    action:
      "Master F&B hires and onboards local market staff for the country. This team handles client onboarding, field support and ongoing account management within the market. Hired staff will follow Coca-Cola HBC BDs with everything they need on a daily basis.",
    owner: "Master F&B",
    ownerType: "master",
  },
  {
    step: 8,
    action:
      "Coca-Cola HBC BU delivers the materials needed to operate the programme in the market. This includes: campaign banners, pack shots, and visual assets for FORA products, product definitions and approved configurations for the local market, and any other technical or brand requirements specific to the country.",
    owner: "Coca-Cola HBC BU",
    ownerType: "coke",
  },
  {
    step: 9,
    action:
      "Country programme is ready. Coca-Cola HBC BU begins activating outlets and onboarding clients in the market.",
    owner: "Coca-Cola HBC BU country",
    ownerType: "coke",
  },
];

const ownerBadgeStyles = {
  master: "bg-[#000000]/5 text-[#000000]",
  coke: "bg-[#dd260e]/10 text-[#dd260e]",
  both: "bg-amber-50 text-amber-800",
};

export const Onboarding = ({ onNavigate }) => {
  const { t } = useLang();
  return (
    <div data-testid="onboarding-view">
      {/* Title */}
      <div className="mb-10 print:mb-3">
        <span className="cchbc-eyebrow mb-5 print:hidden">{t("Process — 9 Steps")}</span>
        <h1
          className="font-condensed text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-[#000000] mb-4 mt-4 leading-[0.95] print:text-xl print:mb-1 print:mt-0"
          data-testid="onboarding-title"
        >
          {t("Country / Business Unit Onboarding")}
        </h1>
        <p className="text-[#555555] text-base max-w-2xl print:text-xs leading-relaxed">
          {t("Step-by-step process for launching FORA products in a new Coca-Cola HBC BU market. Typical timeline: 8–12 weeks from Step 1 to Go Live.")}
        </p>
      </div>

      {/* Market Progress Tracker */}
      <div className="bg-white border border-[#e8e8e8] rounded-sm p-6 mb-10 print:hidden" data-testid="market-progress">
        <span className="cchbc-eyebrow mb-5">{t("Market Progress")}</span>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
          {[
            { code: "HR", name: "Croatia", step: 9, status: "Live", outlets: "3,488" },
            { code: "SI", name: "Slovenia", step: 9, status: "Live", outlets: "766" },
            { code: "PL", name: "Poland", step: 9, status: "Live", outlets: "786" },
            { code: "BA", name: "Bosnia & Herzegovina", step: 9, status: "Live", outlets: "890" },
            { code: "MK", name: "North Macedonia", step: 9, status: "Live", outlets: "683" },
            { code: "RS", name: "Serbia", step: 9, status: "Live", outlets: "29" },
            { code: "RO", name: "Romania", step: 9, status: "Live", outlets: "29" },
            { code: "AT", name: "Austria", step: 1, status: "Commitment talks", outlets: "—" },
            { code: "MD", name: "Moldova", step: 1, status: "On hold", outlets: "—" },
          ].map((m) => {
            const isCurrentMarket = false;
            const progress = Math.round((m.step / 9) * 100);
            return (
              <div key={m.code} className={`border rounded-sm p-3 ${isCurrentMarket ? "border-[#dd260e] bg-[#dd260e]/5" : "border-[#e8e8e8]"}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#000000]">{m.code}</span>
                  <Badge className={`text-[9px] font-semibold border-transparent rounded-sm px-1.5 py-0 ${m.step === 9 ? "bg-emerald-50 text-emerald-700" : m.status === "Phase 1" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"} hover:opacity-100`}>{t(m.status)}</Badge>
                </div>
                <p className="text-[10px] text-[#555555] mb-2">{t(m.name)}{m.outlets !== "—" ? ` — ${m.outlets} ${t("Outlets").toLowerCase()}` : ""}</p>
                <div className="w-full h-1.5 bg-[#e8e8e8] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${m.step === 9 ? "bg-emerald-500" : m.status === "Phase 1" ? "bg-blue-500" : "bg-[#dd260e]"}`} style={{ width: `${progress}%` }} />
                </div>
                <p className="text-[9px] text-[#555555] mt-1">{m.status === "Phase 1" ? t("Steps 1–3: Defining scope & budget") : `${t("Step")} ${m.step}/9`}</p>
                {(() => {
                  const owner = getOwnerByCountry(m.code);
                  return owner && owner.email ? (
                    <a href={`mailto:${owner.email}`} className="text-[9px] text-[#dd260e] hover:underline mt-1 block truncate">{owner.owner}</a>
                  ) : null;
                })()}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-8 print:mb-3 print:gap-3" data-testid="onboarding-legend">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#000000]" />
          <span className="text-xs font-medium text-[#555555]">Master F&B</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#dd260e]" />
          <span className="text-xs font-medium text-[#555555]">{t("Coca-Cola HBC BU")}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          <span className="text-xs font-medium text-[#555555]">{t("Joint")}</span>
        </div>
      </div>

      {/* Process Table */}
      <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden" data-testid="onboarding-table">
        {/* Table header — hidden on mobile */}
        <div className="hidden sm:grid grid-cols-[48px_1fr_200px] lg:grid-cols-[64px_1fr_240px] print:grid-cols-[40px_1fr_160px] bg-[#000000] text-white">
          <div className="px-3 py-3 lg:px-4 print:px-2 print:py-1.5 text-xs print:text-[9px] font-semibold uppercase tracking-wider">#</div>
          <div className="px-4 py-3 lg:px-5 print:px-3 print:py-1.5 text-xs print:text-[9px] font-semibold uppercase tracking-wider">{t("Action")}</div>
          <div className="px-3 py-3 lg:px-5 print:px-2 print:py-1.5 text-xs print:text-[9px] font-semibold uppercase tracking-wider">{t("Owner")}</div>
        </div>

        {/* Table rows — cards on mobile, grid on desktop */}
        {steps.map((s, idx) => {
          const isLast = idx === steps.length - 1;
          return (
            <div
              key={s.step}
              className={`${!isLast ? "border-b border-[#e8e8e8]" : ""} ${s.highlight ? "bg-[#dd260e]/[0.02]" : ""} hover:bg-[#fafafa] transition-colors`}
              data-testid={`onboarding-step-${s.step}`}
            >
              {/* Mobile layout */}
              <div className="sm:hidden p-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-lg font-black text-[#dd260e]">{s.step}</span>
                  <Badge className={`${ownerBadgeStyles[s.ownerType]} border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100`}>
                    {t(s.owner)}
                  </Badge>
                </div>
                {s.highlight && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <Plug className="w-3 h-3 text-[#dd260e]" strokeWidth={2} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#dd260e]">{t("Technical Integration")}</span>
                  </div>
                )}
                <p className="text-sm text-[#000000] leading-relaxed">{t(s.action)}</p>
              </div>

              {/* Desktop layout */}
              <div className="hidden sm:grid grid-cols-[48px_1fr_200px] lg:grid-cols-[64px_1fr_240px] print:grid-cols-[40px_1fr_160px]">
                <div className="px-3 py-4 lg:px-4 lg:py-5 print:px-2 print:py-1.5 flex items-start">
                  <span className="text-lg lg:text-xl print:text-sm font-black text-[#dd260e]">{s.step}</span>
                </div>
                <div className="px-4 py-4 lg:px-5 lg:py-5 print:px-3 print:py-1.5 border-l border-[#e8e8e8]">
                  {s.highlight && (
                    <div className="flex items-center gap-1.5 mb-2 print:mb-0.5">
                      <Plug className="w-3.5 h-3.5 print:w-2.5 print:h-2.5 text-[#dd260e]" strokeWidth={2} />
                      <span className="text-[10px] print:text-[8px] font-semibold uppercase tracking-wider text-[#dd260e]">
                        {t("Technical Integration")}
                      </span>
                    </div>
                  )}
                  <p className="text-sm print:text-[10px] print:leading-snug text-[#000000] leading-relaxed">{t(s.action)}</p>
                </div>
                <div className="px-3 py-4 lg:px-5 lg:py-5 print:px-2 print:py-1.5 border-l border-[#e8e8e8] flex items-start">
                  <Badge
                    className={`${ownerBadgeStyles[s.ownerType]} border-transparent rounded-sm text-xs print:text-[9px] font-semibold hover:opacity-100 whitespace-normal text-left leading-relaxed`}
                  >
                    {t(s.owner)}
                  </Badge>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Standard deliverables from Master F&B */}
      <div className="mt-12 print:hidden" data-testid="mfb-deliverables">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">{t("For every market")}</span>
          <h2 className="font-condensed text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#000000] mt-3">
            {t("What Master F&B Delivers")}
          </h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl">
            {t("Standard package every Coca-Cola HBC BU receives as part of the country programme — no need to request these separately.")}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Contract template */}
          <div className="bg-white border border-[#e8e8e8] rounded-sm p-6 hover:border-[#000000] transition-colors">
            <div className="w-10 h-10 rounded-sm bg-[#000000] flex items-center justify-center mb-4">
              <FileText className="w-5 h-5 text-white" strokeWidth={1.8} />
            </div>
            <p className="font-condensed text-lg font-bold uppercase tracking-tight text-[#000000] mb-2">{t("Contract template")}</p>
            <p className="text-sm text-[#555555] leading-relaxed">
              {t("Standard business agreement template with commercial terms and product scope — provided at Step 3. Request the latest version from Jelena Juric Pehar —")}{" "}
              <a href="mailto:jelena@master-fb.com" className="text-[#dd260e] hover:underline">
                jelena@master-fb.com
              </a>
              .
            </p>
          </div>

          {/* Joint communication channel */}
          <div className="bg-white border border-[#e8e8e8] rounded-sm p-6 hover:border-[#000000] transition-colors" data-testid="deliverable-channel">
            <div className="w-10 h-10 rounded-sm bg-[#000000] flex items-center justify-center mb-4">
              <MessagesSquare className="w-5 h-5 text-white" strokeWidth={1.8} />
            </div>
            <p className="font-condensed text-lg font-bold uppercase tracking-tight text-[#000000] mb-2">{t("Joint communication channel")}</p>
            <p className="text-sm text-[#555555] leading-relaxed mb-3">
              {t("A shared WhatsApp / Microsoft Teams channel is set up at kickoff. Your dedicated Master F&B contacts:")}
            </p>
            <div className="space-y-2.5">
              {[
                { role: "Business", name: "Ognjen Zlacki", email: "ognjen.zlacki@master-fb.com", wa: "+385 91 640 0506" },
                { role: "Operations", name: "Matej Planinic", email: "matej.planinic@master-fb.com", wa: "+387 63 212 329" },
              ].map((c) => (
                <div key={c.email} className="border-l-2 border-[#dd260e] pl-3">
                  <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#000000]">
                    {t(c.role)} — {c.name}
                  </p>
                  <a href={`mailto:${c.email}`} className="text-xs text-[#dd260e] hover:underline block mt-0.5">
                    {c.email}
                  </a>
                  <a
                    href={`https://wa.me/${c.wa.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#555555] hover:text-[#dd260e] block mt-0.5"
                  >
                    WhatsApp: {c.wa}
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Banner & packshot specs */}
          <div className="bg-white border border-[#e8e8e8] rounded-sm p-6 hover:border-[#000000] transition-colors">
            <div className="w-10 h-10 rounded-sm bg-[#000000] flex items-center justify-center mb-4">
              <Ruler className="w-5 h-5 text-white" strokeWidth={1.8} />
            </div>
            <p className="font-condensed text-lg font-bold uppercase tracking-tight text-[#000000] mb-2">{t("Banner dimensions & packshot specs")}</p>
            <p className="text-sm text-[#555555] leading-relaxed">
              {t("Complete specification sheet — banner dimensions and packshot format requirements — included in the product specifications, so your team can produce campaign visuals that fit every FORA placement.")}
            </p>
          </div>
        </div>
      </div>

      {/* Country Setup CTA */}
      <div
        className="bg-[#000000] text-white rounded-sm p-6 sm:p-8 mt-10 print:hidden flex flex-col sm:flex-row sm:items-center gap-6 justify-between"
        data-testid="country-setup-cta"
      >
        <div>
          <span className="cchbc-eyebrow light mb-3">{t("Ready to start?")}</span>
          <h3 className="font-condensed text-2xl sm:text-3xl font-black uppercase tracking-tight mt-2 mb-2">
            {t("Submit your country setup")}
          </h3>
          <p className="text-sm text-white/70 leading-relaxed max-w-xl">
            {t("Send us your market details, QR Menu requirements and brand assets through the Country Setup form — everything Master F&B needs to start preparing your market.")}
          </p>
        </div>
        <button
          onClick={() => onNavigate("setup")}
          className="cchbc-btn bg-[#dd260e] text-white border-transparent hover:bg-white hover:text-[#000000] shrink-0"
          data-testid="country-setup-cta-btn"
        >
          {t("Open Country Setup")}
        </button>
      </div>

      {/* Timeline visual */}
      <div className="mt-12 mb-10 print:mt-4 print:mb-3">
        <span className="cchbc-eyebrow mb-6 print:hidden">{t("Process Timeline")}</span>
        <p className="hidden print:block text-[8px] font-semibold uppercase tracking-[0.2em] text-[#555555] mb-2">
          {t("Process Timeline")}
        </p>

        {/* Desktop: horizontal */}
        <div className="hidden sm:flex items-center gap-0" data-testid="onboarding-timeline">
          {steps.map((s, idx) => {
            const isLast = idx === steps.length - 1;
            const colors = { master: "bg-[#000000]", coke: "bg-[#dd260e]", both: "bg-amber-500" };
            const labels = ["Present", "Review", "Agreement", "Print agency", "Tech Integration", "Contacts", "Hire team", "Materials", "Go Live"];
            return (
              <div key={s.step} className="flex items-center shrink-0">
                <div className="flex flex-col items-center">
                  {isLast ? (
                    <div className="w-10 h-10 print:w-7 print:h-7 rounded-full bg-emerald-500 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 print:w-3.5 print:h-3.5 text-white" strokeWidth={2} />
                    </div>
                  ) : s.highlight ? (
                    <div className="h-10 px-3 print:h-7 print:px-2 rounded-full bg-amber-500 flex items-center justify-center gap-1">
                      <Plug className="w-4 h-4 print:w-3 print:h-3 text-white" strokeWidth={2} />
                      <span className="text-sm print:text-[9px] font-bold text-white">{s.step}</span>
                    </div>
                  ) : (
                    <div className={`w-10 h-10 print:w-7 print:h-7 rounded-full ${colors[s.ownerType]} flex items-center justify-center`}>
                      <span className="text-sm print:text-[9px] font-bold text-white">{s.step}</span>
                    </div>
                  )}
                  <p className="text-[10px] print:text-[7px] text-[#555555] font-medium mt-2 print:mt-1 max-w-[80px] text-center leading-tight">
                    {t(labels[idx])}
                  </p>
                </div>
                {!isLast && <div className="w-12 lg:w-16 print:w-6 h-[2px] bg-[#e8e8e8] mx-1" />}
              </div>
            );
          })}
        </div>

        {/* Mobile: vertical */}
        <div className="sm:hidden flex flex-col" data-testid="onboarding-timeline-mobile">
          {steps.map((s, idx) => {
            const isLast = idx === steps.length - 1;
            const colors = { master: "bg-[#000000]", coke: "bg-[#dd260e]", both: "bg-amber-500" };
            const labels = ["Present", "Review", "Agreement", "Print agency", "Tech Integration", "Contacts", "Hire team", "Materials", "Go Live"];
            return (
              <div key={s.step} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  {isLast ? (
                    <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2} />
                    </div>
                  ) : s.highlight ? (
                    <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                      <Plug className="w-4 h-4 text-white" strokeWidth={2} />
                    </div>
                  ) : (
                    <div className={`w-9 h-9 rounded-full ${colors[s.ownerType]} flex items-center justify-center shrink-0`}>
                      <span className="text-xs font-bold text-white">{s.step}</span>
                    </div>
                  )}
                  {!isLast && <div className="w-[2px] h-6 bg-[#e8e8e8] my-1" />}
                </div>
                <div className="pt-1.5 pb-4">
                  <p className="text-sm font-semibold text-[#000000]">{t(labels[idx])}</p>
                  <Badge className={`${ownerBadgeStyles[s.ownerType]} border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100 mt-1`}>
                    {s.owner}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Share buttons */}
      <div className="flex flex-wrap items-center justify-end gap-4 mt-10 print:hidden" data-testid="share-section">
        <button
          onClick={() => window.print()}
          className="cchbc-btn cchbc-btn-primary"
          data-testid="download-pdf-btn"
        >
          <Download className="w-3.5 h-3.5" />
          {t("Download PDF")}
        </button>
        <a
          href={`mailto:?subject=${encodeURIComponent("FORA — Country/BU Onboarding Process")}&body=${encodeURIComponent("Hi,\n\nPlease find the FORA Country/Business Unit Onboarding process here:\n" + window.location.href + "\n\nBest regards")}`}
          className="cchbc-btn cchbc-btn-outline"
          data-testid="share-email-btn"
        >
          <Mail className="w-3.5 h-3.5" />
          {t("Email")}
        </a>
      </div>

      <PageFooter />
    </div>
  );
};
